import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../../UI';
import { usePopper } from 'react-popper';
import { SearchAssetsInput } from '../HistoricalIndex/SearchAssetsInput';
import { useTranslation } from 'react-i18next';
import { CalendarFilter } from './types';
import { transformFiltersToCheckbox } from './helpers';
import { debounce, isEmpty } from 'lodash';
import { CheckboxWithLabel } from './Utils';
import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { useQuery } from '@apollo/client';
import { GET_NEWS_CALENDAR_FILTERS } from '../../../http/apollo-queries';
import { Button } from '../../UI';

interface EventsFilterProps {
  width: number;

  onFiltersChange: (selectedFilters: string[]) => void;
}

const emptyFilters = [];

type Filter = {
  isChecked: boolean;
  title: string;
  options: (CalendarFilter & { isChecked: boolean })[];
};

const EventsFilter: React.FC<EventsFilterProps> = ({
  width,
  onFiltersChange,
}) => {
  const { t } = useTranslation();

  // Calendar filters
  const { data: { getNewsCalendarFilters: filtersData = [] } = {}, loading } =
    useQuery<{
      getNewsCalendarFilters: CalendarFilter[];
    }>(GET_NEWS_CALENDAR_FILTERS);

  // Modal
  const [isVisible, setIsVisible] = useState(false);

  const [referenceEl, setReferenceEl] = useState<HTMLDivElement | null>(null);
  const [filtersEl, setFiltersEl] = useState<HTMLDivElement | null>(null);
  const filtersRef = useRef<HTMLDivElement | null>(null);

  const popper = usePopper(referenceEl, filtersEl, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left'],
        },
      },
    ],
  });

  // Filters
  const [filters, setFilters] = useState<Filter[]>([]);
  const [checked, setChecked] =
    useState<{ isRoot: boolean; id: string; parentId: string | null }[]>(
      emptyFilters
    );
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    if (isEmpty(filtersData)) return;
    setFilters(transformFiltersToCheckbox(filtersData) as Filter[]);
  }, [filtersData]);

  useEffect(() => {
    popper.update?.();
  }, [width]);

  const onCheckedChange = useCallback(
    debounce((args) => {
      onFiltersChange(args);
    }, 700),
    []
  );

  useEffect(() => {
    onCheckedChange(checked.filter((el) => !el.isRoot).map((el) => el.id));
  }, [checked, onFiltersChange]);

  useEffect(() => {
    // Reset filters
    if (!searchFilter)
      return setFilters(transformFiltersToCheckbox(filtersData));

    setFilters(
      transformFiltersToCheckbox(
        filters
          .map((filter) => filter.options)
          .flat()
          .filter((el) =>
            el.description.toLowerCase().includes(searchFilter.toLowerCase())
          )
      )
    );
  }, [searchFilter]);

  useOutsideClick(filtersRef, () => {
    setIsVisible(false);
  });

  return (
    <div className={'flex items-center w-full h-full'} ref={filtersRef}>
      {/* POPPER */}
      <div
        ref={setFiltersEl}
        style={popper.styles.popper}
        {...popper.attributes.popper}
        className={`${isVisible ? 'block' : 'hidden'} w-96 h-80 z-100`}
      >
        <div className="w-full h-full bg-white border border-grey-10 rounded-xl flex flex-col dark:bg-grey-90 dark:border-grey-70">
          <SearchAssetsInput
            placeholder={t('widget.economicCalendar.search')}
            onFinishTyping={setSearchFilter}
            loading={loading}
          />
          <div
            className={`w-full flex-1 text-black overflow-y-auto p-4 ${
              loading && 'pointer-events-none bg-grey-backdrop'
            }`}
          >
            <Button
              onClick={() => setChecked(emptyFilters)}
              variant={'secondary'}
            >
              {t('widget.economicCalendar.removeFilters')}
            </Button>

            <div>
              {filters.map((filter, i) => {
                const isRootChecked =
                  checked.findIndex((el) => el.id === filter.title) !== -1;
                return (
                  <div key={i}>
                    <div className="flex w-full items-center justify-between mt-1 py-2">
                      <CheckboxWithLabel
                        isChecked={isRootChecked}
                        onClick={() => {
                          if (isRootChecked) {
                            // Remove all checkboxes from this category
                            const otherOnes = checked
                              .filter((el) => el.parentId !== filter.title)
                              .filter((el) => el.id !== filter.title);
                            setChecked(otherOnes);
                            return;
                          } else {
                            // Mark all as checked for this category
                            setChecked((prev) => [
                              // Save root as marked
                              {
                                isRoot: true,
                                id: filter.title,
                                parentId: null,
                              },
                              // Remove old items from this category
                              ...prev.filter(
                                (f) =>
                                  filter.options.findIndex(
                                    (el) => el.code === f.id
                                  ) === -1
                              ),
                              ...filter.options.map((el) => ({
                                isRoot: false,
                                id: el.code,
                                parentId: filter.title,
                              })),
                            ]);
                          }
                        }}
                      >
                        <p
                          className={
                            'text-black dark:text-white font-medium uppercase tracking-wide text-sm'
                          }
                        >
                          {filter.title}
                        </p>
                      </CheckboxWithLabel>
                    </div>
                    {filter.options.map((option, i) => {
                      const isOptionChecked =
                        checked.findIndex((el) => el.id === option.code) !== -1;

                      return (
                        <div
                          className="flex items-center justify-between py-2"
                          key={i}
                        >
                          <CheckboxWithLabel
                            isChecked={isRootChecked || isOptionChecked}
                            onClick={() => {
                              if (isOptionChecked && isRootChecked) {
                                // Remove filter and parent
                                return setChecked((prev) =>
                                  prev
                                    .filter((el) => el.id !== option.code)
                                    .filter((el) => el.id !== filter.title)
                                );
                              } else if (isOptionChecked) {
                                // Only option is checked, not parent
                                return setChecked((prev) =>
                                  prev.filter((el) => el.id !== option.code)
                                );
                              } else {
                                // Option is not checked
                                const isAllOptionsCheckedInCategory =
                                  checked.filter(
                                    (el) => el.parentId === filter.title
                                  ).length ===
                                  filters.find(
                                    (el) => el.title === filter.title
                                  )!.options.length -
                                    1;

                                const newState = [
                                  {
                                    isRoot: false,
                                    id: option.code,
                                    parentId: filter.title,
                                  },

                                  ...checked,
                                ];
                                if (isAllOptionsCheckedInCategory) {
                                  newState.push({
                                    isRoot: true,
                                    id: filter.title,
                                    parentId: null,
                                  });
                                }
                                setChecked(newState);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <p className={'text-black dark:text-white'}>
                                {option.description}
                              </p>
                            </div>
                          </CheckboxWithLabel>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* REFERENCE ELEMENT */}
      <div
        className="w-full h-full flex items-center justify-between pl-4 cursor-pointer"
        ref={setReferenceEl}
        onClick={() => setIsVisible((prev) => !prev)}
      >
        {width === 1 ? (
          <Icon svgName="filter" />
        ) : (
          <div className="w-full items-center justify-between flex">
            <div className="flex items-center gap-3">
              <Icon svgName="filter" />
              <p className="text-sm">Filters</p>
              <div className="w-6 h-6 bg-grey-5 flex items-center justify-center rounded-full text-grey-70">
                {checked.filter((el) => !el.isRoot).length}
              </div>
            </div>
            <div>
              <Icon svgName="arrowDown" className="dark:text-white" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsFilter;
